// components/NavBar.js
// BuzzInApp — Barra de navegación inferior (mobile-first)
//
// Props:
//   activeScreen  "home" | "levels" | "profile"
//   onNavigate    (screen) => void
//   unreadCount   number (opcional) — badge en profile si hay disputas pendientes

import React from "react";
import useTranslation from "../i18n/useTranslation";
import "./NavBar.css";

// ─── Íconos SVG inline (sin dependencias externas) ───────────────────────────
const IconHome = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 10.5L12 3L21 10.5V21H15V15H9V21H3V10.5Z"
      fill={active ? "var(--accent)" : "none"}
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const IconLevels = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect
      x="3" y="14" width="4" height="7" rx="1"
      fill={active ? "var(--accent)" : "none"}
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
    />
    <rect
      x="10" y="9" width="4" height="12" rx="1"
      fill={active ? "var(--accent)" : "none"}
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
    />
    <rect
      x="17" y="4" width="4" height="17" rx="1"
      fill={active ? "var(--accent)" : "none"}
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
    />
  </svg>
);

const IconProfile = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle
      cx="12" cy="8" r="4"
      fill={active ? "var(--accent)" : "none"}
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
    />
    <path
      d="M4 20C4 17 7.6 15 12 15C16.4 15 20 17 20 20"
      stroke={active ? "var(--accent)" : "var(--text-secondary)"}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ICONS = {
  home:    IconHome,
  levels:  IconLevels,
  profile: IconProfile,
};

// ─── Componente ───────────────────────────────────────────────────────────────
const NavBar = ({ activeScreen = "home", onNavigate, unreadCount = 0 }) => {
  const { t } = useTranslation();

  const tabs = [
    { id: "home",    label: t("nav.home")    },
    { id: "levels",  label: t("nav.levels")  },
    { id: "profile", label: t("nav.profile") },
  ];

  return (
    <nav className="navbar" role="navigation" aria-label="Navegación principal">
      {tabs.map(({ id, label }) => {
        const Icon    = ICONS[id];
        const isActive = activeScreen === id;
        const hasBadge = id === "profile" && unreadCount > 0;

        return (
          <button
            key={id}
            className={`navbar__tab ${isActive ? "navbar__tab--active" : ""}`}
            onClick={() => onNavigate?.(id)}
            aria-current={isActive ? "page" : undefined}
            aria-label={label}
          >
            <span className="navbar__icon-wrap">
              <Icon active={isActive} />
              {hasBadge && (
                <span className="navbar__badge" aria-label={`${unreadCount} pendientes`}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </span>
            <span className="navbar__label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default NavBar;
